/**
 * Centralized API service for the corporate website.
 * All CMS data is served from the unified admin backend.
 *
 * Token: App.tsx fetches a public JWT from /token on load and stores it
 * in localStorage("access_token"). Pass it in Authorization header only
 * for endpoints that require it (blogs, blog-details, comment).
 */

const BASE = process.env.REACT_APP_API_URL as string;

/** Read stored public JWT — safe to call before App init (returns null) */
const getToken = (): string | null => localStorage.getItem("access_token");

const PROD_API_FALLBACK = "https://api.mindsyncx.com/api";

/** Generic fetch wrapper */
async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  withAuth = false
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (withAuth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  try {
    const res = await fetch(`${BASE}${path}`, { ...options, headers });
    if (!res.ok) throw new Error(`API error ${res.status}: ${path}`);
    return res.json() as Promise<T>;
  } catch (err) {
    if (BASE && BASE.includes("localhost") && BASE !== PROD_API_FALLBACK) {
      try {
        const fallbackRes = await fetch(`${PROD_API_FALLBACK}${path}`, { ...options, headers });
        if (fallbackRes.ok) {
          return (await fallbackRes.json()) as T;
        }
      } catch {
        // ignore and let original throw
      }
    }
    throw err;
  }
}

// ── Public CMS endpoints ──────────────────────────────────────────────────────

/** GET /faqs — returns Faq[] */
export const getFaqs = () => apiFetch<any[]>("/faqs");

/** GET /services — returns ServiceSection (header + services[]) */
export const getServices = () => apiFetch<any>("/services");

/** GET /service-details/:id */
export const getServiceDetail = (id: string) =>
  apiFetch<any>(`/service-details/${id}`);

/** GET /platform-products — the enterprise catalog the new pages render */
export const getPlatformProducts = () => apiFetch<any[]>("/platform-products");

/** GET /solutions — both cuts; the site groups them by `cut` */
export const getSolutions = () => apiFetch<any[]>("/solutions");

/** GET /offices — footer addresses */
export const getOffices = () => apiFetch<any[]>("/offices");

/** GET /seo — one record per route path */
export const getSeoMeta = () => apiFetch<any[]>("/seo");

/** GET /certifications — footer certification strip */
export const getCertifications = () => apiFetch<any[]>("/certifications");

/** GET /trust — 204 while the security page is unpublished */
export const getTrust = () => apiFetch<any>("/trust");

/** GET /site-content/:section — returns { main: data } */
export const getSiteContent = (section: string) =>
  apiFetch<any>(`/site-content/${section}`);

/** GET /blog-details — returns BlogDetail[] (all, requires auth token) */
export const getAllBlogDetails = () => apiFetch<any[]>("/blog-details", {}, true);

/* The legacy /products and /product-details endpoints backed a portfolio
   showcase that the platform catalogue replaced. Nothing on the site reads them
   any more — see getPlatformProducts above. */

/** GET /blogs — returns Blog[] (requires auth token) */
export const getBlogs = () => apiFetch<any[]>("/blogs", {}, true);

/** GET /blog-details/:id — returns BlogDetail[] (requires auth token) */
export const getBlogDetail = (id: string) =>
  apiFetch<any[]>(`/blog-details/${id}`, {}, true);

/** POST /contact */
export const postContact = (data: Record<string, string>) =>
  apiFetch<any>("/contact", { method: "POST", body: JSON.stringify(data) });

/** POST /comment (requires auth token) */
export const postComment = (data: Record<string, string>) =>
  apiFetch<any>("/comment", { method: "POST", body: JSON.stringify(data) }, true);

/** POST /newsletter */
export const postNewsletter = (email: string) =>
  apiFetch<any>("/newsletter", { method: "POST", body: JSON.stringify({ email }) });

/** POST /track — fire-and-forget, never throws */
export const trackPageview = (page: string): void => {
  const token = getToken();
  fetch(`${BASE}/track`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ page, type: "pageview" }),
  }).catch(() => {
    // silently fail — tracking must never break the page
  });
};
